/**
 * Skeleton Loading Components
 *
 * Theme-aware skeleton placeholders that match final content shapes.
 * Uses CSS classes from vortx-design-system.css for animations.
 *
 * @module components/ux/Skeleton
 */

import React from 'react';
import { Box } from '@mui/material';

/**
 * Base skeleton component
 *
 * @param {Object} props
 * @param {string} [props.variant='text'] - Shape variant: 'text', 'heading', 'card', 'avatar', 'button', 'input', 'rectangular', 'circular'
 * @param {string|number} [props.width] - Custom width
 * @param {string|number} [props.height] - Custom height
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.sx] - MUI sx prop for additional styling
 */
export function Skeleton({
    variant = 'text',
    width,
    height,
    className = '',
    sx = {},
    ...props
}) {
    const variantClass = {
        text: 'vx-skeleton-text',
        heading: 'vx-skeleton-heading',
        card: 'vx-skeleton-card',
        avatar: 'vx-skeleton-avatar',
        button: 'vx-skeleton-button',
        input: 'vx-skeleton-input',
        rectangular: '',
        circular: '',
    }[variant] || '';

    return (
        <Box
            className={`vx-skeleton ${variantClass} ${className}`}
            sx={{
                width: width,
                height: height,
                borderRadius: variant === 'circular' ? '50%' : undefined,
                ...sx,
            }}
            {...props}
        />
    );
}

/**
 * Text skeleton with length variants
 */
export function SkeletonText({ length = 'medium', lines = 1, spacing = 0.5, ...props }) {
    const lengthClass = {
        short: 'short',
        medium: 'medium',
        long: 'long',
        full: '',
    }[length] || '';

    if (lines === 1) {
        return (
            <Skeleton
                variant="text"
                className={`vx-skeleton-text ${lengthClass}`}
                {...props}
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
            {Array.from({ length: lines }).map((_, i) => (
                <Skeleton
                    key={i}
                    variant="text"
                    className={`vx-skeleton-text ${i === lines - 1 ? 'short' : lengthClass}`}
                    {...props}
                />
            ))}
        </Box>
    );
}

/**
 * Card skeleton - mimics a typical card layout
 */
export function SkeletonCard({ hasImage = false, hasActions = false, sx = {} }) {
    return (
        <Box
            className="vx-skeleton"
            sx={{
                p: 2,
                borderRadius: 'var(--border-radius-md)',
                ...sx,
            }}
        >
            {hasImage && (
                <Skeleton
                    variant="rectangular"
                    height={140}
                    sx={{ mb: 2, borderRadius: 'var(--border-radius-sm)' }}
                />
            )}
            <Skeleton variant="heading" sx={{ mb: 1 }} />
            <SkeletonText lines={2} length="long" />
            {hasActions && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Skeleton variant="button" />
                    <Skeleton variant="button" width={80} />
                </Box>
            )}
        </Box>
    );
}

/**
 * List item skeleton
 */
export function SkeletonListItem({ hasAvatar = false, hasSecondary = true, sx = {} }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                ...sx,
            }}
        >
            {hasAvatar && <Skeleton variant="avatar" />}
            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" className="vx-skeleton-text medium" />
                {hasSecondary && (
                    <Skeleton variant="text" className="vx-skeleton-text short" sx={{ mt: 0.5 }} />
                )}
            </Box>
        </Box>
    );
}

/**
 * Table row skeleton
 */
export function SkeletonTableRow({ columns = 4, sx = {} }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                borderBottom: '1px solid var(--border-subtle)',
                ...sx,
            }}
        >
            {Array.from({ length: columns }).map((_, i) => (
                <Box key={i} sx={{ flex: i === 0 ? 2 : 1 }}>
                    <Skeleton variant="text" />
                </Box>
            ))}
        </Box>
    );
}

/**
 * Form field skeleton
 */
export function SkeletonFormField({ hasLabel = true, sx = {} }) {
    return (
        <Box sx={{ mb: 2, ...sx }}>
            {hasLabel && (
                <Skeleton
                    variant="text"
                    className="vx-skeleton-text short"
                    sx={{ mb: 0.5, height: '0.75em' }}
                />
            )}
            <Skeleton variant="input" />
        </Box>
    );
}

/**
 * Dashboard metric card skeleton
 */
export function SkeletonMetricCard({ sx = {} }) {
    return (
        <Box
            className="vx-skeleton"
            sx={{
                p: 2,
                borderRadius: 'var(--border-radius-md)',
                minHeight: 100,
                ...sx,
            }}
        >
            <Skeleton variant="text" className="vx-skeleton-text short" sx={{ mb: 1, height: '0.75em' }} />
            <Skeleton variant="heading" sx={{ width: '60%', height: '2em', mb: 0.5 }} />
            <Skeleton variant="text" className="vx-skeleton-text medium" sx={{ height: '0.75em' }} />
        </Box>
    );
}

/**
 * Chart/graph skeleton
 */
export function SkeletonChart({ height = 300, sx = {} }) {
    return (
        <Box
            className="vx-skeleton"
            sx={{
                height,
                borderRadius: 'var(--border-radius-md)',
                position: 'relative',
                overflow: 'hidden',
                ...sx,
            }}
        >
            {/* Fake axis lines */}
            <Box
                sx={{
                    position: 'absolute',
                    left: 40,
                    right: 20,
                    bottom: 30,
                    top: 20,
                    borderLeft: '1px solid rgba(128,128,128,0.2)',
                    borderBottom: '1px solid rgba(128,128,128,0.2)',
                }}
            />
        </Box>
    );
}

/**
 * Scenario card skeleton (matches your app's scenario cards)
 */
export function SkeletonScenarioCard({ sx = {} }) {
    return (
        <Box
            className="vx-skeleton"
            sx={{
                p: 2,
                borderRadius: 'var(--border-radius-md)',
                ...sx,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Skeleton variant="heading" sx={{ width: '50%' }} />
                <Skeleton variant="button" width={60} height={24} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <SkeletonMetricCard sx={{ flex: 1, minHeight: 60, p: 1 }} />
                <SkeletonMetricCard sx={{ flex: 1, minHeight: 60, p: 1 }} />
                <SkeletonMetricCard sx={{ flex: 1, minHeight: 60, p: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="button" />
                <Skeleton variant="button" width={80} />
            </Box>
        </Box>
    );
}

/**
 * Project card skeleton
 */
export function SkeletonProjectCard({ sx = {} }) {
    return (
        <Box
            className="vx-skeleton"
            sx={{
                p: 2,
                borderRadius: 'var(--border-radius-md)',
                ...sx,
            }}
        >
            <Skeleton variant="heading" sx={{ width: '70%', mb: 1 }} />
            <Skeleton variant="text" className="vx-skeleton-text medium" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 10 }} />
                <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: 10 }} />
            </Box>
            <Skeleton variant="text" className="vx-skeleton-text short" />
        </Box>
    );
}

/**
 * Wrapper that shows skeleton or content based on loading state
 */
export function SkeletonWrapper({
    isLoading,
    skeleton,
    children,
    minDisplayMs = 300,
}) {
    const [showSkeleton, setShowSkeleton] = React.useState(isLoading);
    const startTimeRef = React.useRef(null);

    React.useEffect(() => {
        if (isLoading) {
            setShowSkeleton(true);
            startTimeRef.current = Date.now();
        } else if (startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = Math.max(0, minDisplayMs - elapsed);

            if (remaining > 0) {
                const timer = setTimeout(() => setShowSkeleton(false), remaining);
                return () => clearTimeout(timer);
            } else {
                setShowSkeleton(false);
            }
        } else {
            setShowSkeleton(false);
        }
    }, [isLoading, minDisplayMs]);

    return showSkeleton ? skeleton : children;
}

export default Skeleton;
